import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { LightingFixture, MarkerSizesConfig } from '@/types/floorPlanViewer';
import { useAppStore, useAIToolsState } from '@/stores/appStore';
import {
  FixtureIconRenderer,
  type FixtureIconData,
} from '@/components/floor-plans/FixtureIconRenderer';
import { useMarkerSizes } from '@/contexts/MarkerSizeContext';
import { useLightingEffects } from '@/contexts/LightingEffectsContext';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/logger';
import { updateFixtureRotation, updateFixture } from '@/services/fixtureService';
import { ProductImageCallout } from './ProductImageCallout';
import { useProduct } from '@/features/product-library/hooks/useProductLibrary';
import { toast } from 'sonner';
import {
  useMergedCategories,
  type MergedCategory,
} from '@/features/product-library/hooks/useMergedCategories';

type DraggedEntityType = 'fixture' | 'control' | 'panel' | 'room';

interface DragState {
  type: DraggedEntityType;
  id: string;
  startPos: { x: number; y: number };
  currentPos: { x: number; y: number };
}

interface FixtureLayerProps {
  fixtures: LightingFixture[];
  scale: number;
  onStartDrag?: (
    type: DraggedEntityType,
    id: string,
    e: React.MouseEvent | PointerEvent,
    elementPos?: { x: number; y: number },
  ) => void;
  isDragging?: boolean;
  dragState?: DragState | null;
  wasRecentlyDragged?: (elementId: string) => boolean;
  isPublicView?: boolean;
}

const fixtureCategoryKey = (category?: string | null, subcategory?: string | null) =>
  `${category || ''}:${subcategory || ''}`;

const getFixtureIconData = (category: MergedCategory): FixtureIconData | null => {
  if (category.icon_svg) {
    return {
      svg: category.icon_svg,
      url: undefined,
      color: category.fill_color || '#6B7280',
      metadata: category.original?.metadata || {},
    };
  }

  return null;
};

export const FixtureLayer = memo(
  ({
    fixtures,
    scale,
    onStartDrag,
    isDragging = false,
    dragState,
    wasRecentlyDragged,
    isPublicView,
  }: FixtureLayerProps) => {
    const selectedFixtures = useAppStore(state => state.selection.fixtures);
    const selectedControls = useAppStore(state => state.selection.controls);
    const hoveredEntityId = useAppStore(state => state.ui.hoveredEntityId);
    const hoveredEntityType = useAppStore(state => state.ui.hoveredEntityType);
    const selectEntity = useAppStore(state => state.selectEntity);
    const selectMultipleEntities = useAppStore(state => state.selectMultipleEntities);
    const setHoveredEntity = useAppStore(state => state.setHoveredEntity);
    const setActiveDrawer = useAppStore(state => state.setActiveDrawer);
    const setActiveAnnotationType = useAppStore(state => state.setActiveAnnotationType);
    const setActiveMode = useAppStore(state => state.setActiveMode);
    const viewMode = useAppStore(state => state.ui.viewMode);
    const circuits = useAppStore(state => state.selection.circuits);
    const activeTool = useAppStore(state => state.ui.toolbar.activeTool);
    const activeMode = useAppStore(state => state.ui.toolbar.activeMode);
    // Unassigned filter flag from toolbar
    const showOnlyUnassigned = useAppStore(state => state.ui.toolbar.showOnlyUnassigned);

    // Get AI tools state for opacity and confidence filtering
    const aiTools = useAIToolsState();

    // Check if we're in multi-selection mode (more than one entity selected across all types)
    const totalSelections = selectedFixtures.size + selectedControls.size;
    const isMultiSelection = totalSelections > 1;

    // Rotation state
    const [rotatingFixtureId, setRotatingFixtureId] = useState<string | null>(null);
    const [currentRotationAngle, setCurrentRotationAngle] = useState<number>(0);
    const rotationRef = useRef<{
      startAngle: number;
      startRotation: number;
      lastAngle: number;
      totalDelta: number;
    } | null>(null);

    // ✅ Lighting effects
    const { sim } = useLightingEffects();

    // Get marker size from context
    const { sizes } = useMarkerSizes();
    const rawMarkerSize = sizes.fixtures;
    const { data: fixtureCategories = [] } = useMergedCategories('lighting_fixtures');
    const fixtureIconDataByPath = useMemo(() => {
      const iconMap = new Map<string, FixtureIconData>();

      fixtureCategories.forEach(category => {
        const iconData = getFixtureIconData(category);
        if (iconData) {
          iconMap.set(fixtureCategoryKey(category.category, category.subcategory), iconData);
        }
      });

      return iconMap;
    }, [fixtureCategories]);

    const getPreloadedFixtureIcon = useCallback(
      (fixture: LightingFixture): FixtureIconData | null =>
        fixtureIconDataByPath.get(fixtureCategoryKey(fixture.category, fixture.subcategory)) ||
        fixtureIconDataByPath.get(fixtureCategoryKey(fixture.category, null)) ||
        null,
      [fixtureIconDataByPath],
    );

    // Convert marker size to expected string format
    const getMarkerSizeString = useCallback(
      (size: number | string): 'small' | 'medium' | 'large' => {
        if (typeof size === 'string') {
          return size as 'small' | 'medium' | 'large';
        }
        // Convert pixel size to size string
        if (size <= 16) return 'small';
        if (size <= 24) return 'medium';
        return 'large';
      },
      [],
    );

    const markerSize = useMemo(
      () => getMarkerSizeString(rawMarkerSize),
      [rawMarkerSize, getMarkerSizeString],
    );

    // Get the circuits data from the floor plan context
    const { data } = useFloorPlanContext();
    const circuitsData = data.circuits || [];
    const controlsData = data.controls || [];

    // Get fixtures base scale from floor plan metadata (auto-calculated from image dimensions)
    // Default to 1.0 for existing floor plans without auto-scale applied
    const markerSizes = data.floorPlanMetadata?.marker_sizes as MarkerSizesConfig | null;
    const fixturesBaseScale = markerSizes?.fixtures_base_scale ?? 1.0;

    // Get circuits that are assigned to selected controls
    const selectedControlCircuits = useMemo(() => {
      const circuits = new Set<string>();
      if (selectedControls.size > 0) {
        controlsData.forEach(control => {
          if (selectedControls.has(control.id) && control.circuit_id) {
            circuits.add(control.circuit_id);
          }
        });
      }
      return circuits;
    }, [selectedControls, controlsData]);

    // Get circuits that contain selected fixtures
    const selectedFixtureCircuits = useMemo(() => {
      const circuits = new Set<string>();
      // Only populate when exactly one fixture is selected (for single selection highlighting)
      if (selectedFixtures.size === 1) {
        fixtures.forEach(fixture => {
          if (selectedFixtures.has(fixture.id) && fixture.circuit_id) {
            circuits.add(fixture.circuit_id);
          }
        });
      }
      return circuits;
    }, [selectedFixtures, fixtures]);

    const handleFixtureClick = useCallback(
      (fixtureId: string, e: React.MouseEvent | PointerEvent) => {
        e.stopPropagation();
        // Don't preventDefault as it can interfere with Safari's event handling

        // Check if this element was recently dragged
        if (wasRecentlyDragged && wasRecentlyDragged(fixtureId)) {
          logger.log('Blocked fixture click - was recently dragged');
          return;
        }

        // Get current toolbar state from store
        const toolbarState = useAppStore.getState().ui.toolbar;
        const isCircuitMode = toolbarState.activeMode === 'circuit';

        logger.log(`Fixture ${fixtureId} clicked - circuit mode: ${isCircuitMode}`);

        if (isCircuitMode) {
          // In circuit mode, toggle fixture selection
          if (selectedFixtures.has(fixtureId)) {
            // Remove from selection
            const newSelection = new Set(selectedFixtures);
            newSelection.delete(fixtureId);
            selectMultipleEntities('fixture', Array.from(newSelection));
          } else {
            // Add to selection
            const newSelection = new Set(selectedFixtures);
            newSelection.add(fixtureId);
            selectMultipleEntities('fixture', Array.from(newSelection));
          }
          // Don't open any drawer in circuit mode - the circuit drawer should stay open
          return;
        }

        // Normal mode behavior
        if (e.shiftKey && selectedFixtures.size > 0) {
          // Multi-select with shift - add to existing selection
          const newSelection = new Set(selectedFixtures);
          newSelection.add(fixtureId);
          selectMultipleEntities('fixture', Array.from(newSelection));
        } else {
          // Single select - replace selection
          selectEntity('fixture', fixtureId);

          // Clear any active placement tools when clicking an element
          setActiveAnnotationType(null);
          setActiveMode('default');
        }

        // Check total selections across all entity types
        const state = useAppStore.getState();
        const totalSelections =
          state.selection.fixtures.size +
          state.selection.controls.size +
          state.selection.products.size +
          state.selection.annotations.size +
          state.selection.rooms.size;

        // Only skip drawer if ALL selections are annotations (they use context toolbar)
        const hasOnlyAnnotations =
          state.selection.annotations.size > 0 &&
          state.selection.annotations.size === totalSelections;

        // Open appropriate drawer based on selection count
        if (totalSelections > 1 && !hasOnlyAnnotations) {
          setActiveDrawer('selection');
        } else {
          setActiveDrawer('fixture');
        }
      },
      [selectedFixtures, selectEntity, selectMultipleEntities, setActiveDrawer, wasRecentlyDragged],
    );

    const handleMouseEnter = useCallback(
      (fixtureId: string) => {
        if (!isDragging) {
          setHoveredEntity(fixtureId, 'fixture');
        }
      },
      [setHoveredEntity, isDragging],
    );

    const handleMouseLeave = useCallback(() => {
      if (!isDragging) {
        setHoveredEntity(null, null);
      }
    }, [setHoveredEntity, isDragging]);

    const suppressNextMouseDownRef = useRef(false);

    const beginDrag = useCallback(
      (
        fixtureId: string,
        fixturePos: { x: number; y: number },
        e: React.MouseEvent | PointerEvent,
      ) => {
        // Only start drag on left mouse button
        if ((e as any).button !== 0) return;

        // Don't preventDefault in mousedown - it prevents click events in Safari!
        // Only stopPropagation to prevent bubbling
        e.stopPropagation();

        // Get current tool from store
        const activeTool = useAppStore.getState().ui.toolbar.activeTool;

        // Only allow dragging when select tool is active
        if (onStartDrag && activeTool === 'select') {
          onStartDrag('fixture', fixtureId, e, fixturePos);
        }
      },
      [onStartDrag],
    );

    const handleMouseDown = useCallback(
      (
        fixtureId: string,
        fixturePos: { x: number; y: number },
        e: React.MouseEvent | PointerEvent,
      ) => {
        if (suppressNextMouseDownRef.current) {
          suppressNextMouseDownRef.current = false;
          return;
        }

        beginDrag(fixtureId, fixturePos, e);
      },
      [beginDrag],
    );

    // Touch event handlers
    const touchTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
    const touchStartPositions = useRef<Map<string, { x: number; y: number }>>(new Map());

    const handleTouchStart = useCallback(
      (fixtureId: string, fixturePos: { x: number; y: number }, e: React.TouchEvent) => {
        // Only handle single touch
        if (e.touches.length !== 1) return;

        // Don't preventDefault - it prevents the click event from being synthesized
        e.stopPropagation();

        const touch = e.touches[0];
        const startPos = { x: touch.clientX, y: touch.clientY };
        touchStartPositions.current.set(fixtureId, startPos);

        // Set up press-and-hold timer (500ms)
        const timer = setTimeout(() => {
          // Trigger haptic feedback if available
          if ('vibrate' in navigator) {
            navigator.vibrate(10);
          }

          // Start drag mode
          const activeTool = useAppStore.getState().ui.toolbar.activeTool;
          if (onStartDrag && activeTool === 'select') {
            // Create a synthetic mouse event for compatibility with existing drag handler
            const syntheticEvent = {
              preventDefault: () => {},
              stopPropagation: () => {},
              clientX: touch.clientX,
              clientY: touch.clientY,
              button: 0,
            } as React.MouseEvent;

            onStartDrag('fixture', fixtureId, syntheticEvent as any, fixturePos);

            // Remove the timer since we've started dragging
            touchTimers.current.delete(fixtureId);
          }
        }, 500);

        touchTimers.current.set(fixtureId, timer);
      },
      [onStartDrag],
    );

    const handleTouchEnd = useCallback(
      (fixtureId: string, e: React.TouchEvent) => {
        // Don't preventDefault here as it breaks Safari's touch-to-click conversion
        e.stopPropagation();

        // Clear the press-and-hold timer
        const timer = touchTimers.current.get(fixtureId);
        if (timer) {
          clearTimeout(timer);
          touchTimers.current.delete(fixtureId);

          // If timer was cleared, it means we didn't start dragging, so treat as tap
          const startPos = touchStartPositions.current.get(fixtureId);
          if (startPos && e.changedTouches && e.changedTouches.length > 0) {
            const endTouch = e.changedTouches[0];
            const deltaX = Math.abs(endTouch.clientX - startPos.x);
            const deltaY = Math.abs(endTouch.clientY - startPos.y);

            // If finger didn't move much, treat as tap to select
            if (deltaX < 10 && deltaY < 10) {
              // Create synthetic mouse event for click handler with all required properties
              const syntheticEvent = {
                stopPropagation: () => {},
                preventDefault: () => {},
                shiftKey: false,
                button: 0,
                currentTarget: e.currentTarget,
                target: e.target,
                clientX: endTouch.clientX,
                clientY: endTouch.clientY,
                screenX: endTouch.screenX,
                screenY: endTouch.screenY,
              } as React.MouseEvent;

              handleFixtureClick(fixtureId, syntheticEvent);
            }
          }
        }

        touchStartPositions.current.delete(fixtureId);
      },
      [handleFixtureClick],
    );

    const handleTouchMove = useCallback((fixtureId: string, e: React.TouchEvent) => {
      // Cancel press-and-hold timer if finger moves too much
      const timer = touchTimers.current.get(fixtureId);
      if (timer) {
        const startPos = touchStartPositions.current.get(fixtureId);
        if (startPos && e.touches && e.touches.length > 0) {
          const currentTouch = e.touches[0];
          const deltaX = Math.abs(currentTouch.clientX - startPos.x);
          const deltaY = Math.abs(currentTouch.clientY - startPos.y);

          // Cancel timer if moved more than 10 pixels
          if (deltaX > 10 || deltaY > 10) {
            clearTimeout(timer);
            touchTimers.current.delete(fixtureId);
          }
        }
      }
    }, []);

    const handleRotationStart = useCallback(
      (fixture: LightingFixture, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        // Prevent fixture click and drag
        const fixtureElement = document.querySelector(
          `[data-fixture-id="${fixture.id}"]`,
        ) as HTMLElement;
        if (fixtureElement) {
          fixtureElement.dataset.rotating = 'true';
        }

        // Get the fixture element center
        const fixtureRect = fixtureElement?.getBoundingClientRect();
        if (!fixtureRect) return;

        const centerX = fixtureRect.left + fixtureRect.width / 2;
        const centerY = fixtureRect.top + fixtureRect.height / 2;

        const startAngle = (Math.atan2(e.clientY - centerY, e.clientX - centerX) * 180) / Math.PI;

        setRotatingFixtureId(fixture.id);
        const initialRotation = fixture.rotation || 0;
        setCurrentRotationAngle(initialRotation);

        // Initialize rotation tracking
        rotationRef.current = {
          startAngle,
          startRotation: initialRotation,
          lastAngle: startAngle,
          totalDelta: 0,
        };

        // Store the latest rotation value in closure
        let latestRotation = initialRotation;

        const handleMouseMove = (e: MouseEvent) => {
          if (!rotationRef.current) return;

          const currentAngle =
            (Math.atan2(e.clientY - centerY, e.clientX - centerX) * 180) / Math.PI;

          // Calculate angle difference from last position
          let angleDiff = currentAngle - rotationRef.current.lastAngle;

          // Handle wrapping at -180/180 boundary
          if (angleDiff > 180) {
            angleDiff -= 360;
          } else if (angleDiff < -180) {
            angleDiff += 360;
          }

          // Update total delta
          rotationRef.current.totalDelta += angleDiff;
          rotationRef.current.lastAngle = currentAngle;

          // Calculate new rotation
          const newRotation = rotationRef.current.startRotation + rotationRef.current.totalDelta;

          // Store the raw rotation for saving
          let saveRotation = newRotation % 360;
          if (saveRotation < 0) saveRotation += 360;

          // Apply snapping to display rotation
          let displayRotation = newRotation;
          if (!e.shiftKey) {
            const snapAngles = [0, 45, 90, 135, 180, 225, 270, 315];
            const snapThreshold = 5; // degrees

            const normalizedRotation = ((newRotation % 360) + 360) % 360;
            for (const snapAngle of snapAngles) {
              if (Math.abs(normalizedRotation - snapAngle) < snapThreshold) {
                displayRotation =
                  rotationRef.current.startRotation +
                  rotationRef.current.totalDelta -
                  (normalizedRotation - snapAngle);
                saveRotation = snapAngle;
                break;
              }
            }
          }

          // Store latest rotation for saving
          latestRotation = saveRotation;

          // Update state for real-time display
          setCurrentRotationAngle(saveRotation);

          // Update visual rotation using the display rotation for smooth animation
          const iconContainer = document.querySelector(
            `[data-fixture-id="${fixture.id}"] [data-icon-container]`,
          ) as HTMLElement;
          if (iconContainer) {
            // Preserve current scale from DOM (may be different from saved if user is resizing)
            const currentTransform = iconContainer.style.transform;
            const scaleMatch = currentTransform.match(/scale\(([\d.]+)\)/);
            const currentScale = scaleMatch ? parseFloat(scaleMatch[1]) : fixture.scale || 1.0;
            iconContainer.style.transition = 'none';
            iconContainer.style.transform = `scale(${currentScale}) rotate(${displayRotation}deg)`;
          }
        };

        const handleMouseUp = async () => {
          // Get final rotation value from closure
          const finalRotation = latestRotation;

          setRotatingFixtureId(null);
          rotationRef.current = null;

          // Remove rotating flag
          if (fixtureElement) {
            delete fixtureElement.dataset.rotating;
          }

          // Ensure the visual rotation matches the saved value immediately
          const iconContainer = document.querySelector(
            `[data-fixture-id="${fixture.id}"] [data-icon-container]`,
          ) as HTMLElement;
          if (iconContainer) {
            // Preserve current scale from DOM (may be different from saved if user is resizing)
            const currentTransform = iconContainer.style.transform;
            const scaleMatch = currentTransform.match(/scale\(([\d.]+)\)/);
            const currentScale = scaleMatch ? parseFloat(scaleMatch[1]) : fixture.scale || 1.0;
            const currentTransition = iconContainer.style.transition;
            iconContainer.style.transition = 'none';
            iconContainer.style.transform = `scale(${currentScale}) rotate(${finalRotation}deg)`;

            // Force reflow
            void iconContainer.offsetHeight;

            // Restore transition
            iconContainer.style.transition = currentTransition;
          }

          // Save to database
          try {
            await updateFixtureRotation(fixture.id, finalRotation);
            await data.refetch();
          } catch (error) {
            logger.error('Failed to update fixture rotation:', error);
          }

          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      },
      [data],
    );

    // Filter to only point fixtures (exclude linear/strip fixtures)
    // Linear fixtures are handled by StripLayer
    const pointFixtures = fixtures.filter(
      f => !f.fixture_form_factor || f.fixture_form_factor === 'point',
    );

    logger.log('[FixtureLayer] Rendering with fixtures:', {
      count: pointFixtures.length,
      total: fixtures.length,
      fixturesWithIcons: pointFixtures.filter(f => f.category && f.subcategory).length,
      fixtures: pointFixtures.slice(0, 3), // Log first 3 fixtures
    });

    return (
      <div className="absolute inset-0" style={{ pointerEvents: 'none' }}>
        {pointFixtures.map(fixture => {
          const isSelected = selectedFixtures.has(fixture.id);
          const isHovered = hoveredEntityId === fixture.id;
          const hasCircuit = !!fixture.circuit_id;
          const isCircuitMode = viewMode === 'circuits';
          const isBeingDragged = dragState?.type === 'fixture' && dragState?.id === fixture.id;

          // Apply confidence filtering for AI-detected fixtures
          // Manually placed fixtures (confidence_score === 1 or user_verified) always show
          const isManuallyPlaced = fixture.confidence_score === 1 || fixture.user_verified === true;
          const isAIDetected =
            !isManuallyPlaced && fixture.confidence_score != null && fixture.confidence_score < 1;

          // Always apply confidence filtering for AI-detected fixtures
          // This ensures the slider works even after restart
          if (isAIDetected) {
            const confidence = fixture.confidence_score || 0;
            const meetsConfidenceThreshold = confidence >= aiTools.confidenceThreshold;

            // Skip rendering if confidence is too low
            if (!meetsConfidenceThreshold) {
              return null;
            }
          }

          // Apply unassigned filter: Show ONLY fixtures WITHOUT a circuit when the filter is enabled
          if (showOnlyUnassigned && hasCircuit) {
            return null; // Skip fixtures that already have a circuit assigned
          }

          // Find circuit data for badge
          const circuit = fixture.circuit_id
            ? circuitsData.find(c => c.id === fixture.circuit_id)
            : null;

          // Generate circuit color only if circuit exists and we want to override fixture colors
          // For now, don't generate circuit colors to preserve icon colors from admin panel
          const circuitColor = null; // We can enable this later: circuit ? getCircuitColor(circuit.id) : null;

          // Check if fixture is in selected circuit
          const isInSelectedCircuit = fixture.circuit_id ? circuits.has(fixture.circuit_id) : false;

          // Check if this fixture belongs to a circuit being hovered in a drawer
          // Only apply hover effect in single selection mode (not during multi-selection)
          const isInHoveredCircuit =
            hoveredEntityType === 'circuit' &&
            hoveredEntityId &&
            fixture.circuit_id === hoveredEntityId &&
            !isMultiSelection;

          // Check if this fixture belongs to a circuit that's assigned to a selected control
          const isInSelectedControlCircuit =
            fixture.circuit_id && selectedControlCircuits.has(fixture.circuit_id);

          // Check if this fixture belongs to same circuit as selected fixture
          const isInSameCircuitAsSelected =
            fixture.circuit_id && selectedFixtureCircuits.has(fixture.circuit_id);

          // ✅ Lighting simulation logic
          const isSimLit =
            sim.running && fixture.circuit_id && sim.circuits.includes(fixture.circuit_id);

          // When simulation is running but this fixture is not lit, dim it
          const shouldDimDuringSimulation = sim.running && !isSimLit;

          // Calculate marker size in pixels for badge sizing
          const markerSizePixels = typeof rawMarkerSize === 'number' ? rawMarkerSize : 20;

          return (
            <div
              key={fixture.id}
              data-fixture-id={fixture.id}
              className={cn('absolute transition-all duration-200 select-none', {
                'cursor-pointer': true,
                'cursor-grab': !isDragging && !isBeingDragged && activeTool === 'select',
                'cursor-grabbing': isBeingDragged,
                'shadow-lg z-[100]': isBeingDragged,
                'scale-110': isSelected && !isBeingDragged,
                'opacity-50': isDragging && !isBeingDragged,
              })}
              style={{
                position: 'absolute',
                left: fixture.x_position,
                top: fixture.y_position,
                pointerEvents: 'auto',
                zIndex: isSimLit
                  ? 500
                  : isSelected
                    ? 100
                    : isHovered
                      ? 50
                      : isBeingDragged
                        ? 200
                        : 10,
                // Safari fix: Apply transform using WebKit prefix as well
                // Note: Removed scale(1.5) on selection - just show ring without scaling
                transform: 'translate(-50%, -50%)',
                WebkitTransform: 'translate(-50%, -50%)',
                // Safari fix: ensure touch-action is set
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                touchAction: 'manipulation',
              }}
              onMouseEnter={() => handleMouseEnter(fixture.id)}
              onMouseLeave={handleMouseLeave}
              onMouseDown={e =>
                handleMouseDown(fixture.id, { x: fixture.x_position, y: fixture.y_position }, e)
              }
              onClick={e => handleFixtureClick(fixture.id, e)}
              onPointerDown={e => {
                // For Safari, also handle pointer events
                if (e.pointerType === 'mouse' && e.button === 0) {
                  suppressNextMouseDownRef.current = true;
                  window.setTimeout(() => {
                    suppressNextMouseDownRef.current = false;
                  }, 0);

                  const isSafari =
                    navigator.userAgent.includes('Safari') &&
                    !navigator.userAgent.includes('Chrome');

                  if (!wasRecentlyDragged?.(fixture.id)) {
                    const startX = e.clientX;
                    const startY = e.clientY;
                    let didStartDrag = false;
                    const pointerDownEvent = e.nativeEvent;

                    const handlePointerMove = (moveEvent: PointerEvent) => {
                      const deltaX = Math.abs(moveEvent.clientX - startX);
                      const deltaY = Math.abs(moveEvent.clientY - startY);

                      // If moved more than 5px, it's a drag
                      if (deltaX > 5 || deltaY > 5) {
                        didStartDrag = true;
                        beginDrag(
                          fixture.id,
                          { x: fixture.x_position, y: fixture.y_position },
                          pointerDownEvent,
                        );
                        // Clean up listeners
                        document.removeEventListener('pointermove', handlePointerMove);
                        document.removeEventListener('pointerup', handlePointerUp);
                        document.removeEventListener('pointercancel', handlePointerUp);
                      }
                    };

                    const handlePointerUp = (upEvent: PointerEvent) => {
                      document.removeEventListener('pointermove', handlePointerMove);
                      document.removeEventListener('pointerup', handlePointerUp);
                      document.removeEventListener('pointercancel', handlePointerUp);

                      if (isSafari && !didStartDrag) {
                        handleFixtureClick(fixture.id, upEvent);
                      }
                    };

                    document.addEventListener('pointermove', handlePointerMove);
                    document.addEventListener('pointerup', handlePointerUp);
                    document.addEventListener('pointercancel', handlePointerUp);

                    // Clean up listeners after 1 second in case events never fire
                    setTimeout(() => {
                      document.removeEventListener('pointermove', handlePointerMove);
                      document.removeEventListener('pointerup', handlePointerUp);
                      document.removeEventListener('pointercancel', handlePointerUp);
                    }, 1000);
                  }
                }
              }}
              onTouchStart={e =>
                handleTouchStart(fixture.id, { x: fixture.x_position, y: fixture.y_position }, e)
              }
              onTouchEnd={e => handleTouchEnd(fixture.id, e)}
              onTouchMove={e => handleTouchMove(fixture.id, e)}
              onContextMenu={e => e.preventDefault()}
            >
              {/* Dual Rendering: Icon or Marker based on category/subcategory */}
              {fixture.category && fixture.subcategory ? (
                // Render as icon if category and subcategory are present
                <FixtureIconRenderer
                  key={`icon-${fixture.id}`}
                  id={fixture.id}
                  category={fixture.category}
                  subcategory={fixture.subcategory}
                  size={markerSize}
                  isSelected={isSelected}
                  isHovered={
                    isHovered ||
                    isInHoveredCircuit ||
                    isInSelectedControlCircuit ||
                    isInSameCircuitAsSelected
                  }
                  hasCircuit={hasCircuit}
                  circuitColor={circuitColor}
                  isInSelectedCircuit={isInSelectedCircuit}
                  isSimLit={isSimLit}
                  simMode={sim.mode}
                  isLinkedCircuit={false}
                  circuitBadge={
                    circuit
                      ? {
                          number: circuit.sequence_number?.toString() || circuit.name || 'C',
                          isHighlighted:
                            !sim.running &&
                            !isMultiSelection &&
                            (isInHoveredCircuit ||
                              isInSelectedCircuit ||
                              isInSelectedControlCircuit ||
                              isInSameCircuitAsSelected),
                          // Include fixture scale for proper badge sizing
                          baseSize: markerSizePixels * (fixture.scale || 1.0) * fixturesBaseScale,
                        }
                      : null
                  }
                  opacity={shouldDimDuringSimulation ? 0.2 : 1.0}
                  showConfidenceLabel={aiTools.showLabels && !fixture.user_verified}
                  confidenceScore={fixture.confidence_score}
                  isCircuitMode={activeMode === 'circuit'}
                  scale={(fixture.scale || 1.0) * fixturesBaseScale}
                  rotation={fixture.rotation}
                  preloadedIconData={getPreloadedFixtureIcon(fixture)}
                  isLoadBlinking={
                    activeMode !== 'circuit' &&
                    !sim.running &&
                    !isMultiSelection &&
                    (isInHoveredCircuit ||
                      isInSelectedCircuit ||
                      isInSelectedControlCircuit ||
                      isInSameCircuitAsSelected)
                  }
                  onClick={e => handleFixtureClick(fixture.id, e)}
                  onMouseEnter={() => handleMouseEnter(fixture.id)}
                  onMouseLeave={handleMouseLeave}
                  onMouseDown={e =>
                    handleMouseDown(fixture.id, { x: fixture.x_position, y: fixture.y_position }, e)
                  }
                />
              ) : null}

              {/* Rotation Handle - show for fixtures with icons when selected */}
              {fixture.category && fixture.subcategory && isSelected && !isPublicView && (
                <>
                  {(() => {
                    // Include base scale in rotation handle positioning
                    const currentFixtureScale = (fixture.scale || 1.0) * fixturesBaseScale;
                    const iconRadius = 24; // base icon radius in pixels
                    const margin = 11; // fixed margin from icon edge
                    const calculatedOffset = iconRadius * currentFixtureScale + margin;
                    const rotationOffset = Math.max(calculatedOffset, 35);

                    return (
                      <div
                        className="absolute"
                        data-rotation-handle="true"
                        data-fixture-rotation-handle={fixture.id}
                        style={{
                          left: '50%',
                          top: `-${rotationOffset}px`,
                          transform: 'translateX(-50%)',
                          pointerEvents: 'auto',
                          zIndex: 1000,
                        }}
                      >
                        {/* Rotation handle button */}
                        <div
                          className="bg-white rounded-full shadow-lg border border-blue-400/80 cursor-grab hover:scale-110 hover:border-blue-500 hover:shadow-xl transition-all flex items-center justify-center"
                          style={{
                            width: '20px',
                            height: '20px',
                            pointerEvents: 'auto',
                          }}
                          onMouseDown={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleRotationStart(fixture, e);
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          onPointerDown={e => {
                            e.stopPropagation();
                          }}
                          onTouchStart={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            const touch = e.touches[0];
                            const mouseEvent = new MouseEvent('mousedown', {
                              clientX: touch.clientX,
                              clientY: touch.clientY,
                              bubbles: true,
                            }) as any;
                            handleRotationStart(fixture, mouseEvent);
                          }}
                        >
                          {/* Rotation icon - elegant curved arrow */}
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            className="text-blue-500"
                          >
                            <path
                              d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C15.0902 3 17.7902 4.58073 19.3499 7"
                              stroke="currentColor"
                              strokeWidth={1.8}
                              strokeLinecap="round"
                            />
                            <path
                              d="M17 3L19.3499 7L15.8499 8.5"
                              stroke="currentColor"
                              strokeWidth={1.8}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>

                        {/* Rotation angle badge */}
                        {rotatingFixtureId === fixture.id && (
                          <div
                            className="absolute bg-gray-900 text-white text-xs font-medium px-2 py-1 rounded shadow-lg pointer-events-none"
                            style={{
                              left: '35px',
                              top: '-5px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {Math.round(currentRotationAngle)}°
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}

              {!fixture.category || !fixture.subcategory ? (
                // Render traditional marker (fallback for fixtures without category/subcategory)
                <div className="relative" style={{ pointerEvents: 'none' }}>
                  {/* Selection Ring - Enhanced for better visibility */}
                  {isSelected && (
                    <div
                      className={cn(
                        'absolute rounded-full border-2 border-dashed animate-pulse',
                        activeMode === 'circuit' ? 'border-green-500' : 'border-blue-500',
                      )}
                      style={{
                        width: `${markerSizePixels + 16}px`,
                        height: `${markerSizePixels + 16}px`,
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: -1,
                      }}
                    />
                  )}

                  {/* Main Fixture Marker */}
                  <div
                    className={cn(
                      'rounded-full border-2 flex items-center justify-center relative transition-all duration-200',
                      // Only apply these classes when NOT in simulation mode
                      !isSimLit && {
                        // Selected state with enhanced styling (only when not lit)
                        'ring-2 ring-offset-1': isSelected,
                        'ring-blue-400': isSelected && activeMode !== 'circuit', // Blue ring for normal selection
                        'ring-green-400': isSelected && activeMode === 'circuit', // Green ring for circuit mode selection
                        'scale-110 shadow-lg': isSelected && !isBeingDragged,
                        // Base colors - these should always apply
                        'bg-blue-500 border-blue-700': !hasCircuit, // Unassigned = blue
                        'bg-green-500 border-green-700': hasCircuit, // Assigned = green
                        // Dimmed when simulation running but not lit
                        'bg-gray-600 border-gray-700': shouldDimDuringSimulation,
                        // Hover effect (only when not in simulation)
                        'ring-1 ring-gray-300': isHovered && !isSelected && !sim.running,
                        // Red blinking effect when either:
                        //   • the fixture belongs to a circuit being hovered in a drawer
                        //   • the fixture belongs to a circuit that is explicitly selected (e.g. via Presentation Drawer)
                        //   • the fixture belongs to a circuit linked to a selected control
                        //   • the fixture is in the same circuit as the single selected fixture (helps discover related loads)
                        // BUT NOT during circuit mode
                        'animate-slow-blink bg-red-500 border-red-700 ring-2 ring-red-400 ring-offset-1':
                          activeMode !== 'circuit' &&
                          (isInHoveredCircuit ||
                            (!isMultiSelection &&
                              (isInSelectedCircuit ||
                                isInSelectedControlCircuit ||
                                isInSameCircuitAsSelected))) &&
                          !sim.running,
                      },
                    )}
                    style={{
                      width: `${markerSizePixels}px`,
                      height: `${markerSizePixels}px`,
                      // ✅ Complete style override when lit (same as original implementation)
                      ...(isSimLit && sim.mode === 'on'
                        ? {
                            // ON mode - warm 2700K lighting effect with more transparency and spread
                            backgroundColor: 'rgba(255, 241, 224, 0.85)', // More transparent center
                            opacity: 1,
                            boxShadow: `
                      0 0 60px 30px rgba(255, 241, 224, 0.5),
                      0 0 120px 60px rgba(255, 228, 196, 0.35),
                      0 0 180px 90px rgba(255, 214, 170, 0.25),
                      0 0 240px 120px rgba(255, 197, 143, 0.15),
                      0 0 320px 160px rgba(255, 180, 107, 0.08),
                      0 0 400px 200px rgba(255, 166, 87, 0.04)
                    `,
                            border: `1px solid rgba(255, 241, 224, 0.4)`,
                            transform: `scale(1.02)`,
                            backfaceVisibility: 'hidden' as const,
                            zIndex: 500,
                            willChange: 'transform, opacity, z-index',
                            animation: 'light-on 0.6s ease-out forwards',
                          }
                        : isSimLit && sim.mode === 'off'
                          ? {
                              // OFF mode - warm 2700K lighting effect fading out with more transparency
                              backgroundColor: 'rgba(255, 241, 224, 0.85)',
                              opacity: 1,
                              boxShadow: `
                      0 0 60px 30px rgba(255, 241, 224, 0.5),
                      0 0 120px 60px rgba(255, 228, 196, 0.35),
                      0 0 180px 90px rgba(255, 214, 170, 0.25),
                      0 0 240px 120px rgba(255, 197, 143, 0.15),
                      0 0 320px 160px rgba(255, 180, 107, 0.08),
                      0 0 400px 200px rgba(255, 166, 87, 0.04)
                    `,
                              border: `1px solid rgba(255, 241, 224, 0.4)`,
                              transform: `scale(1.02)`,
                              backfaceVisibility: 'hidden' as const,
                              zIndex: 500,
                              willChange: 'transform, opacity, z-index',
                              animation: 'light-off 0.6s ease-in forwards',
                            }
                          : {
                              // Normal opacity when not lit
                              opacity:
                                isInHoveredCircuit && !isMultiSelection
                                  ? 1
                                  : shouldDimDuringSimulation
                                    ? 0.4
                                    : aiTools.markerOpacity,
                            }),
                    }}
                  >
                    {/* Inner dot for better visibility */}
                    {!isSimLit && (
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          opacity: 1,
                        }}
                      />
                    )}
                  </div>

                  {/* Circuit Badge for Marker Fixtures - Scales with fixture size */}
                  {circuit &&
                    (() => {
                      // Calculate effective visual scale for the badge
                      const effectiveFixtureScale = (fixture.scale || 1.0) * fixturesBaseScale;
                      const effectiveBadgeSize = markerSizePixels * effectiveFixtureScale;
                      const isHighlighted =
                        !sim.running &&
                        !isMultiSelection &&
                        (isInHoveredCircuit ||
                          isInSelectedCircuit ||
                          isInSelectedControlCircuit ||
                          isInSameCircuitAsSelected);

                      return (
                        <div
                          className={cn(
                            'absolute bg-white border-2 rounded-sm font-bold shadow-md pointer-events-none z-10 transition-all duration-150',
                            {
                              'border-red-500 bg-red-50 text-red-700 animate-slow-blink':
                                isHighlighted,
                              'border-gray-400': !isHighlighted,
                            },
                          )}
                          style={{
                            left: `${effectiveBadgeSize * 0.5}px`,
                            top: `-${effectiveBadgeSize * 0.5}px`,
                            fontSize: `${Math.max(8, effectiveBadgeSize * 0.3)}px`,
                            minWidth: `${effectiveBadgeSize * 0.6}px`,
                            padding: `${effectiveBadgeSize * 0.05}px ${effectiveBadgeSize * 0.15}px`,
                            textAlign: 'center',
                            color: isHighlighted ? '#dc2626' : '#374151',
                            transform: isHighlighted ? 'scale(1.1)' : 'scale(1)',
                          }}
                        >
                          {circuit.sequence_number?.toString() || circuit.name || 'C'}
                        </div>
                      );
                    })()}
                </div>
              ) : null}

              {/* Show dragging indicator */}
              {isBeingDragged && (
                <div className="absolute -inset-2 border-2 border-blue-500 border-dashed rounded-full animate-pulse" />
              )}
            </div>
          );
        })}

        {/* Product Image Callouts for fixtures */}
        {pointFixtures
          .filter(fixture => fixture.show_product_image && fixture.product_id)
          .map(fixture => (
            <FixtureCalloutWrapper
              key={`callout-${fixture.id}`}
              fixture={fixture}
              scale={scale}
              isSelected={selectedFixtures.has(fixture.id)}
              isPublicView={isPublicView}
              onRefetch={data.refetch}
              dragState={dragState}
            />
          ))}
      </div>
    );
  },
);

FixtureLayer.displayName = 'FixtureLayer';

/**
 * Wrapper component to render a fixture product image callout
 */
const FixtureCalloutWrapper = memo(
  ({
    fixture,
    scale,
    isSelected,
    isPublicView,
    onRefetch,
    dragState,
  }: {
    fixture: LightingFixture;
    scale: number;
    isSelected: boolean;
    isPublicView: boolean;
    onRefetch: () => void;
    dragState?: DragState | null;
  }) => {
    const { data: linkedProduct } = useProduct(fixture.product_id || undefined);

    // Get image URL from various sources
    const imageUrl = linkedProduct?.image_url || (linkedProduct?.portal_metadata as any)?.imageUrl;

    if (!imageUrl) {
      return null;
    }

    // Calculate live icon position if this fixture is being dragged
    const isBeingDragged = dragState?.type === 'fixture' && dragState.id === fixture.id;
    const iconPosition =
      isBeingDragged && dragState?.currentPos
        ? { x: dragState.currentPos.x, y: dragState.currentPos.y }
        : undefined;

    // Adapt fixture to LVProduct-like shape for ProductImageCallout
    const productLike = {
      id: fixture.id,
      name: linkedProduct?.name || fixture.fixture_type,
      x_position: fixture.x_position,
      y_position: fixture.y_position,
      product_image_x: fixture.product_image_x,
      product_image_y: fixture.product_image_y,
      product_image_width: fixture.product_image_width,
      product_image_height: fixture.product_image_height,
    };

    const handlePositionChange = async (x: number, y: number) => {
      try {
        await updateFixture(fixture.id, {
          product_image_x: x,
          product_image_y: y,
        } as any);
        onRefetch();
      } catch (error) {
        logger.error('Failed to update image position:', error);
        toast.error('Failed to update image position');
      }
    };

    const handleSizeChange = async (width: number, height: number) => {
      try {
        await updateFixture(fixture.id, {
          product_image_width: width,
          product_image_height: height,
        } as any);
        onRefetch();
      } catch (error) {
        logger.error('Failed to update image size:', error);
        toast.error('Failed to update image size');
      }
    };

    const handleClose = async () => {
      try {
        await updateFixture(fixture.id, {
          show_product_image: false,
        } as any);
        onRefetch();
      } catch (error) {
        logger.error('Failed to hide image callout:', error);
        toast.error('Failed to hide image callout');
      }
    };

    return (
      <ProductImageCallout
        product={productLike as any}
        imageUrl={imageUrl}
        scale={scale}
        isSelected={isSelected}
        isPublicView={isPublicView}
        onPositionChange={handlePositionChange}
        onSizeChange={handleSizeChange}
        onClose={handleClose}
        iconPosition={iconPosition}
      />
    );
  },
);

FixtureCalloutWrapper.displayName = 'FixtureCalloutWrapper';
